import HapticsButtonWrapper from "@Components/generic/haptics-button-wrapper";
import Icon from "@Components/generic/icon";
import useI18n from "@i18n";
import { Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import { TrainingFocus } from "@Shared/schemas/progression/progressionSchemas";

interface ITrainingFocusContent {
  children: React.ReactNode;
  metric: TrainingFocus["metric"] | null;
  isLoading: boolean;
  onClick: () => void;
}

const TrainingFocusContent = ({ children, metric, isLoading, onClick }: ITrainingFocusContent) => {
  const { i18n } = useI18n();

  return (
    <Stack direction="row" alignItems="center" gap={2.5} width="100%">
      <Icon dsIcon="osiris" size={28} color="vrYellow.v300" />
      {children}
      <HapticsButtonWrapper disabled={isLoading}>
        <Button
          variant="contained"
          disabled={isLoading}
          data-shape="pill"
          onClick={onClick}
          size="small"
          startIcon={isLoading ? <CircularProgress size={20} /> : undefined}
        >
          {i18n(metric ? "progress.training-focus.improve" : "progress.training-focus.analyze")}
        </Button>
      </HapticsButtonWrapper>
    </Stack>
  );
};

const renderMetricContent = (
  metric: TrainingFocus["metric"] | null,
  isLoading: boolean,
  onClick: () => void,
  i18n: ReturnType<typeof useI18n>["i18n"],
  i18nWithComponents: ReturnType<typeof useI18n>["i18nWithComponents"]
) => (
  <Box gridArea="1 / 1">
    <TrainingFocusContent metric={metric} isLoading={isLoading} onClick={onClick}>
      <Stack flex={1} gap={0.5}>
        <Typography variant="textLg400" color="common.white">
          {metric
            ? i18nWithComponents("progress.training-focus.focus", {
                metric: (
                  <Typography component="span" variant="inherit" fontWeight="bold">
                    {i18n(`metric.${metric}.name`)}
                  </Typography>
                ),
              })
            : i18n("progress.training-focus.empty.title")}
        </Typography>
        <Typography variant="textBase400" color="text.secondary">
          {i18n(metric ? "progress.training-focus.based-on" : "progress.training-focus.empty.description")}
        </Typography>
      </Stack>
    </TrainingFocusContent>
  </Box>
);

export default renderMetricContent;
